// src/products/dataroom/components/Modals/EmbedDataroomModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { X, FolderPlus, AlertCircle, Loader2 } from 'lucide-react';
import embedService from '../../api/embedService';
import { supabase } from '../../../../config';

const EmbedDataroomModal = ({ isOpen, onClose, currentProjectId, onSuccess, onMessage }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [folderName, setFolderName] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setSelectedProjectId('');
    setFolderName('');
    setError('');
  };

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    setError('');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const availableProjects = await embedService.getEmbeddableProjects(currentProjectId, user.id);
      setProjects(availableProjects);

      if (availableProjects.length === 0) {
        setError('No other datarooms available to embed. You need creator access to embed datarooms.');
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load available datarooms: ' + err.message);
    } finally {
      setLoadingProjects(false);
    }
  }, [currentProjectId]);

  useEffect(() => {
    if (isOpen) {
      loadProjects();
      resetForm();
    }
  }, [isOpen, loadProjects]);

  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    setSelectedProjectId(projectId);
    
    // Auto-fill folder name with project name
    if (projectId) {
      const selectedProject = projects.find(p => p.id === parseInt(projectId));
      if (selectedProject) {
        setFolderName(selectedProject.project_name);
      }
    } else {
      setFolderName('');
    }
    setError('');
  };

  const validateForm = () => {
    if (!selectedProjectId) {
      setError('Please select a dataroom to embed');
      return false;
    }
    
    if (!folderName.trim()) {
      setError('Please enter a folder name');
      return false;
    }

    if (folderName.trim().length < 2) {
      setError('Folder name must be at least 2 characters');
      return false;
    }

    if (folderName.trim().length > 100) {
      setError('Folder name must be less than 100 characters');
      return false;
    }

    return true;
  };

  const handleEmbed = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const result = await embedService.embedDataroom(
        currentProjectId,
        parseInt(selectedProjectId),
        folderName.trim(),
        user.id
      );

      // Success feedback
      const selectedProject = projects.find(p => p.id === parseInt(selectedProjectId));
      const message = `Successfully embedded "${selectedProject?.project_name || 'dataroom'}" as "${folderName.trim()}"`;
      
      onMessage && onMessage(message, 'success');
      onSuccess && onSuccess(result);
      onClose();
    } catch (err) {
      console.error('Embed error:', err);
      setError(err.message || 'Failed to embed dataroom');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const selectedProject = projects.find(p => p.id === parseInt(selectedProjectId));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold flex items-center text-gray-900">
            <FolderPlus className="h-5 w-5 mr-2 text-blue-600" />
            Embed Dataroom
          </h2>
          <button 
            onClick={handleClose} 
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Error Display */}
          {error && (
            <div className="flex items-start p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {/* Loading Projects */}
          {loadingProjects && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-2" />
              <span className="text-gray-600">Loading available datarooms...</span>
            </div>
          )}

          {/* Project Selection */}
          {!loadingProjects && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Dataroom to Embed
              </label>
              <select
                value={selectedProjectId}
                onChange={handleProjectChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                disabled={loading || projects.length === 0}
              >
                <option value="">Choose a dataroom...</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.project_name}
                    {project.description && ` - ${project.description.substring(0, 50)}${project.description.length > 50 ? '...' : ''}`}
                  </option>
                ))}
              </select>
              
              {projects.length === 0 && !loadingProjects && (
                <p className="text-sm text-gray-500 mt-1">
                  No datarooms available. You need creator access to embed datarooms.
                </p>
              )}
            </div>
          )}

          {/* Folder Name Input */}
          {!loadingProjects && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Folder Name
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                value={folderName}
                onChange={(e) => {
                  setFolderName(e.target.value);
                  setError('');
                }}
                placeholder="e.g., Board Reports"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                disabled={loading}
                maxLength={100}
              />
              <div className="flex justify-between mt-1">
                <p className="text-xs text-gray-500">
                  This name will appear as a folder in your dataroom
                </p>
                <p className="text-xs text-gray-400">
                  {folderName.length}/100
                </p>
              </div>
            </div>
          )}

          {/* Selected Project Info */}
          {selectedProject && (
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <div className="flex items-start">
                <FolderPlus className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-medium text-blue-800">Embedding: {selectedProject.project_name}</h4>
                  {selectedProject.description && (
                    <p className="text-sm text-blue-700 mt-1">{selectedProject.description}</p>
                  )}
                  <p className="text-sm text-blue-700 mt-2">
                    <strong>Note:</strong> The embedded content will stay in sync with the original dataroom. 
                    Users will see the embedded content based on their permissions in the original dataroom.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleEmbed}
            disabled={loading || loadingProjects || !selectedProjectId || !folderName.trim() || projects.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Embedding...
              </>
            ) : (
              <>
                <FolderPlus className="h-4 w-4 mr-2" />
                Embed Dataroom
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmbedDataroomModal;